// generic renderer of ips data (workmanagement, cdr, property, crm, assets (later)) 
import $ = require("jquery");
import _ = require("underscore");
import ol = require("ol");
import JSV = require("../utils/jsv");
import MapView = require("../views/MapView");
import Portal = require("portal-gis");
import Utils = require("../utils");
import geo = require("../utils/geo");
import Services = require("../services/services");
import IPS = require("publicsector");
import SymbologyUtils = require("../utils/symbology");
import Converter = require("../utils/ol3/converter");

const fields = ["xCoordinate", "yCoordinate"];
const extentFilterTemplate = _.template("{filter:[{property:xcoordinate,operator:GreaterThan,value:<%=x1%>},{property:xcoordinate,operator:LessThan,value:<%=x2%>},{property:ycoordinate,operator:GreaterThan,value:<%=y1%>},{property:ycoordinate,operator:LessThan,value:<%=y2%>}]}");

function isPoint(item) {
    if (fields.some(f => typeof item[f] === "undefined")) return false;
    if (fields.every(f => item[f] === 0)) return false;
    return true;
}

var sequence = 0;

class Handler {

    static about = `
Query IPS REST services and render the results as layers.

Usage Example:

    <LAYER disabled="False" id="cdr" text="CDR" type="app/layer-factory/group">
    <LAYERS>
        <LAYER disabled="False" id="cdr.application.building" text="Building Applications" type="app/layer-factory/ips-query-layer">
        <OPTIONS>
            <Values id="group-service" value="cdr/applicationtypes/building" />
        </OPTIONS>
        </LAYER>
        <LAYER disabled="True" id="cdr.application.use" text="Use Applications" type="app/layer-factory/ips-query-layer">
        <OPTIONS>
            <Values id="group-service" value="cdr/applicationtypes/use" />
        </OPTIONS>
        </LAYER>
        <LAYER disabled="False" id="cdr.application.codeenforcement" text="Case" type="app/layer-factory/ips-query-layer">
        <OPTIONS>
            <Values id="group-service" value="cdr/applicationtypes/codeenforcement" />
        </OPTIONS>
        </LAYER>
        <LAYER disabled="True" id="cdr.application.businesslicense" text="Business License" type="app/layer-factory/ips-query-layer">
        <OPTIONS>
            <Values id="group-service" value="cdr/applicationtypes/businesslicense" />
        </OPTIONS>
        </LAYER>
    </LAYERS>
    <OPTIONS>
        <Values id="group-service-query" value="{select:[code]}" />
        <Values id="service-query-template" value="{filter:{property:&quot;applicationType.Code&quot;,operator:&quot;Equal&quot;,value:&quot;&lt;%=code%&gt;&quot;}}" />
        <Values id="text-template" value="&lt;%=code%&gt; (&lt;%=type%&gt;)" />
    </OPTIONS>
    </LAYER>

    `;

    static options = {
        "group-service": {
            "about": "The IPS endpoints service to query",
        },
        "group-service-query": {
            "about": "Filter to apply to the group-service",
        },
        "layerType": {
            "about": "Service type to query (only queries webmap services).",
            "domain": "group|ips",
            "default": "ips"
        },
        "locatingproperties": {
            "about": "One or more attribute paths to probe for location information",
            "default": "address",
        },
        "max-record-count": {
            "about": "Maximum number of results to accept",
            "default": "100",
        },
        "merge": {
            "about": "Forwarded to REST but purpose is unclear",
            "default": "true",
        },
        "service-query-template": {
            "about": "_group_ only, used to produce a service query",
        },
        "text-template": {
            "about": "template used to assign _text_ to the child layers",
        },
        "use-gis": {
            "about": "Directs the services use the underlying GIS system, false to use local coordinates stored in IPS.",
            "default": "true",
            "domain": "true|false",
        },
    }

    // if group-service exists then get all the types and create subLayers for each type
    private static group(layer: Portal.MapLayerLeaf, view: MapView) {
        var d = $.Deferred<ol.layer.Base>();
        var options = Utils.asDictionary(layer.Options.Values);

        Utils.ifOption(options, "group-service", groupService => {
            Utils.ifOption(options, "service-query-template", queryTemplate => {

                var api = {
                    query: {
                        merge: true
                    }
                };

                Utils.ifOption(options, "group-service-query", groupServiceQuery => api.query["query"] = groupServiceQuery);

                var service = new Services.BusinessService<any>(api, groupService);

                // get layers to appear in the layer selection tool (could be a race...better to use api (layer model))
                // but how to get api from here?
                //layer.Layers = layer.Layers || { Layers: [] };

                let eachItemReq = service.each();
                eachItemReq.done(items => {
                    var layers = items.map(item => {
                        var childLayer = Utils.deep(layer);
                        childLayer.id = `${layer.id}-group-${sequence++}`;
                        childLayer.text = childLayer.id;
                        Utils.ifOption(options, "text-template", textTemplate => childLayer.text = _.template(textTemplate)(item));
                        let query = _.template(queryTemplate)(item);
                        Utils.setOption(childLayer.Options.Values, {
                            "service-query-template": query
                        });
                        //layer.Layers.Layers.unshift(childLayer);
                        return Handler.ips(childLayer, view);
                    });

                    let allLayersReady = Utils.when(layers);
                    allLayersReady.done(layers => {
                        var groupOptions = <olx.layer.GroupOptions>{
                            id: layer.id,
                            title: layer.text,
                            layers: layers
                        };

                        var group = new ol.layer.Group(groupOptions);
                        d.resolve(group);
                    });
                    allLayersReady.fail(err => {
                        console.error("unable to create all layers", err);
                        d.reject(err);
                    });
                });
                eachItemReq.fail(err => {
                    console.error("unable to create group layer:", layer);
                    d.reject(err);
                });
            });
        });

        return d;
    }

    private static ips(layer: Portal.MapLayerLeaf, view: MapView): ol.layer.Layer {

        var options = _.defaults(Utils.asDictionary(layer.Options.Values), {
            "max-record-count": "100",
            "locatingproperties": "address",
            "use-gis": "true",
            "merge": "true"
        });

        var serviceEndpoint = options["service"];

        let outerQuery = {
            query: <any>{},
            page: `[{Start:1,Max:${options["max-record-count"]}]`,
            usegis: "true" === options["use-gis"],
            merge: "true" === options["merge"]
        };

        Utils.ifOption(options, "service-query-template", v => outerQuery.query = JSV.parse(_.template(v)(layer)));

        Utils.ifOption(options, "extent", (extentStr: string) => {
            let extent = Utils.split(extentStr).map(parseFloat);

            let helper = {
                x1: extent[0], x2: extent[0],
                y1: extent[1], y2: extent[1]
            };

            Utils.ifOption(options, "srs", srs => {

                // reproject to proper srs
                if (srs !== "EPSG:4326") {
                    let t = geo.transformer("EPSG:4326", srs);
                    let min = t.point([extent[0], extent[1]]);
                    let max = t.point([extent[2], extent[3]]);
                    helper = {
                        x1: min[0], x2: max[0],
                        y1: min[1], y2: max[1]
                    };
                }
            });

            if (outerQuery.usegis) {
                // MapExtent={Max:{X:-115.22819045931101,Y:36.17559325598355,Z:0.0},Min:{X:-115.23765832185747,Y:36.17129999423301,Z:0.0}}
                outerQuery["MapExtent"] = `{Max:{X:${helper.x2},Y:${helper.y2},Z:0.0},Min:{X:${helper.x1},Y:${helper.y1},Z:0.0}}`;
            } else {
                let query2 = JSV.parse(extentFilterTemplate(helper));
                let query = outerQuery.query;
                if (!query.filter) { query.filter = query2.filter; } else {
                    if (!_.isArray(query.filter)) query.filter = [query.filter];
                    query.filter = query.filter.concat(query2.filter);
                }
            }
        });

        outerQuery.query = JSV.stringify(outerQuery.query);

        var service = new Services.BusinessService<IPS.GeoEntity>({ query: outerQuery }, serviceEndpoint);

        var vectorSource = new ol.source.Vector({
            features: []
        });

        var vectorLayer = new ol.layer.Vector({
            source: vectorSource,
            visible: "false" !== options["visible"],
            id: layer.id,
            title: layer.text,
        });

        Utils.extend(layer, { loading: true });
        view.mediator.trigger("layerloadstart", {
            layer: layer
        });

        service.each().done(items => {
            let features = items.filter(data => {
                geo.beforeProcess({ data: data }, options["locatingproperties"]);
                return isPoint(data);
            }).map(data => (<Rhythm.GIS.FeatureQueryResultFeature>{
                geometryType: "point",
                geometry: [data.xCoordinate, data.yCoordinate],
                attributes: <any>data,
                symbology: options["symbology"] || options["service"] || layer.id // TODO: should use Utils.extend for feature-level symbology
            }));

            // sorry, but you must transform the feature to 4326 before copying to map
            Utils.ifOption(options, "srs", srs => {
                if (srs !== "EPSG:4326") {
                    let to = new Converter(srs, "EPSG:4326");
                    let noop = new Converter();
                    features = noop.fromNativeFeatures(to.toNativeFeatures({ features: features })).features;
                }
            });

            SymbologyUtils.copyToMap(features, view, vectorSource, options);

        }).always(() => {
            Utils.extend(layer, { loading: false });
            view.mediator.trigger("layerloadend", {
                layer: layer
            });
        });

        return vectorLayer;

    }

    static create(args: { layer: Portal.MapLayerLeaf; view: MapView }): ol.layer.Base | JQueryDeferred<ol.layer.Base> {
        var l = args.layer;
        var options = Utils.asDictionary(l.Options.Values);

        if (options["group-service"] && !options["layerType"]) {
            options["layerType"] = "group";
        }
        var layerType = options["layerType"] || "ips";

        var factory = Handler.ips;
        factory = Handler[layerType] || factory;
        return factory && factory(l, args.view);
    }

}

export = Handler; 